import apiClient from './apiClient';

// Event interface
export interface Event {
  Events_ID: string;
  group_size: number;
  register_start_date: string;
  register_end_date: string;
  vote_end_date: string;
  event_date: string;
  min_select_users: number;
  max_select_users: number;
  round: number;
  status?: number;
  notification_enabled?: boolean;
  created_at?: string;
  updated_at?: string;
  title?: string;
  sub_title?: string;
  Firm_ID?: string;
  Verification_Code?: string;
  Branding: string;
  is_future: boolean;
  postal_code?: string;
  street?: string;
  house_number?: string;
  city?: string;
  country?: string;
  location_name?: string;
}

export interface EventRegistration {
  Registration_ID: string;
  Event_ID: string;
  User_ID: string;
  Registration_Date: string;
  Status: number;
}

// EventView specific interfaces
export interface EventViewData {
  event: Event;
  users: User[];
  registrations: Registration[];
  selections: Selection[];
  votes: Vote[];
  best_matches: RoundData[];
  selections_user_count: number;
  registration_user_count: number;
  votes_count: number;
}

export interface User {
  User_ID: string;
  firstname: string;
  lastname: string;
  title?: string;
  mail: string;
  jobtitle?: string;
  picture?: string;
  profile?: string;
  linkedin?: string;
  firm?: {
    Firm_ID: string;
    abbrev: string;
    name?: string;
    logo?: string;
  };
}

export interface Registration {
  Registration_ID: string;
  User_ID: string;
  Event_ID: string;
  created_at: string;
}

export interface Selection {
  selector_user_id: string;
  selected_user_id: string;
  Event_ID: string;
}

export interface Vote {
  Vote_ID: string;
  Event_ID: string;
  selector_user_id: string;
  selected_user_id: string;
  rank: number;
}

export interface Group {
  group_id: string; // This should always be present from the backend
  Group_ID?: string; // Fallback
  id?: string; // Fallback
  group_members: string[];
  group_size: number;
  created_at?: string;
}

export interface RoundData {
  round_number: number;
  groups: Group[];
}

// TimeTable interfaces
export interface TimeEntry {
  id?: string;
  startTime: string;
  endTime: string;
  type: string;
  description: string;
}

export interface TimeTableResponse {
  entries: TimeEntry[];
  message: string;
}

export interface TimeTableRequest {
  entries: TimeEntry[];
}

// Fetch all events the user is allowed to see
export async function getEvents(): Promise<Event[]> {
  try {
    const response = await apiClient.get<Event[]>('/api/v2/web/events/');
    return response.data || [];
  } catch (error) {
    console.error('Error fetching events:', error);
    return [];
  }
}

// Create a new event
export async function createEvent(eventData: any): Promise<Event | null> {
  try {
    const response = await apiClient.post<Event>('/api/v2/web/events/', eventData);
    // Return the data if present, or a dummy event object if the response was empty
    return response.data || { Events_ID: '' } as Event;
  } catch (error) {
    console.error('Error creating event:', error);
    throw error; // Re-throw to let the component handle the error
  }
}

// Cancel event registration
export async function cancelEventRegistration(eventId: string): Promise<boolean> {
  try {
    await apiClient.delete(`/api/v2/web/events/${eventId}/cancel`);
    return true;
  } catch (error) {
    console.error('Error canceling event registration:', error);
    return false;
  }
}

// Get user event registrations
export async function getUserEventRegistrations(): Promise<EventRegistration[]> {
  try {
    const response = await apiClient.get<EventRegistration[]>(`/api/v2/web/registrations/`);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching user event registrations:', error);
    return [];
  }
}

// Get event view data (all data needed for EventView page)
export async function getEventViewData(eventId: string): Promise<EventViewData> {
  try {
    const response = await apiClient.get<EventViewData>(`/api/v2/web/events/${eventId}/view`);
    return response.data;
  } catch (error) {
    console.error('Error fetching event view data:', error);
    throw error;
  }
}

// Send voting emails for an event
export async function sendVotingEmail(eventId: string): Promise<void> {
  try {
    await apiClient.post(`/api/v2/web/events/${eventId}/registration/mail/event_open/`);
  } catch (error) {
    console.error('Error sending voting emails:', error);
    throw error;
  }
}

// Generate group matches for an event
export async function generateGroupMatches(eventId: string): Promise<void> {
  try {
    await apiClient.post(`/api/v2/web/events/${eventId}/generate-group-matches`);
  } catch (error) {
    console.error('Error generating group matches:', error);
    throw error;
  }
}

// Get group matches for an event
export async function getGroupMatches(eventId: string): Promise<RoundData[]> {
  try {
    const response = await apiClient.get(`/api/v2/web/events/${eventId}/group-matches`);
    return response.data;
  } catch (error) {
    console.error('Error getting group matches:', error);
    throw error;
  }
}

// Generate matches for an event (legacy)
export async function generateMatches(eventId: string): Promise<void> {
  try {
    await apiClient.put(`/api/v2/web/events/${eventId}/matches/`);
  } catch (error) {
    console.error('Error generating matches:', error);
    throw error;
  }
}

// Download Excel file for event
export async function downloadEventExcel(eventId: string): Promise<void> {
  try {
    const response = await apiClient.get(`/api/v2/web/events/${eventId}/view/download`, {
      responseType: 'blob'
    });

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `event_${eventId}_match_results.xlsx`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and download
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading Excel file:', error);
    throw error;
  }
}

// Download votes Excel file for event
export async function downloadVotesExcel(eventId: string): Promise<void> {
  try {
    const response = await apiClient.get(`/api/v2/web/events/${eventId}/votes/export/`, {
      responseType: 'blob'
    });

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `event_${eventId}_votes.xlsx`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and download
    const blob = response.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading votes Excel file:', error);
    throw error;
  }
}

// Delete a specific group
export async function deleteGroup(eventId: string, groupId: string): Promise<void> {
  try {
    await apiClient.delete(`/api/v2/web/events/${eventId}/group-matches/${groupId}`);
  } catch (error) {
    console.error('Error deleting group:', error);
    throw error;
  }
}

// Add user to a specific group
export async function addUserToGroup(eventId: string, groupId: string, userId: string): Promise<void> {
  try {
    // ✅ Fixed: userId should be in URL path, not request body
    await apiClient.post(`/api/v2/web/events/${eventId}/group-matches/${groupId}/add-user/${userId}`);
  } catch (error) {
    console.error('Error adding user to group:', error);
    throw error;
  }
}

// Remove user from a specific group
export async function removeUserFromGroup(eventId: string, groupId: string, userId: string): Promise<void> {
  try {
    await apiClient.delete(`/api/v2/web/events/${eventId}/group-matches/${groupId}/remove-user/${userId}`);
  } catch (error) {
    console.error('Error removing user from group:', error);
    throw error;
  }
}

// Get unmatched users for an event
export async function getUnmatchedUsers(eventId: string, roundNumber?: number): Promise<{
  event_id: string;
  round_number?: number;
  total_registered: number;
  total_matched: number;
  total_unmatched: number;
  unmatched_users: User[];
}> {
  try {
    const url = roundNumber 
      ? `/api/v2/web/events/${eventId}/unmatched-users?round_number=${roundNumber}`
      : `/api/v2/web/events/${eventId}/unmatched-users`;
      
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error getting unmatched users:', error);
    throw error;
  }
}

// Get unregistered users for an event
export async function getUnregisteredUsers(eventId: string): Promise<User[]> {
  try {
    const response = await apiClient.get<{users: User[]}>(`/api/v2/web/events/${eventId}/unregistered-users`);
    return response.data.users || [];
  } catch (error) {
    console.error('Error getting unregistered users:', error);
    return [];
  }
}

// Register a user for an event
export async function registerUserForEvent(eventId: string, userId: string): Promise<boolean> {
  try {
    await apiClient.post(`/api/v2/web/events/${eventId}/register/user/${userId}`);
    return true;
  } catch (error) {
    console.error('Error registering user for event:', error);
    throw error;
  }
}

// Unregister a user from an event
export async function unregisterUserFromEvent(eventId: string, userId: string): Promise<boolean> {
  try {
    await apiClient.delete(`/api/v2/web/events/${eventId}/unregister/user/${userId}`);
    return true;
  } catch (error) {
    console.error('Error unregistering user from event:', error);
    throw error;
  }
}

// Get all registrations for an event
export async function getEventRegistrations(eventId: string): Promise<Registration[]> {
  try {
    const response = await apiClient.get<{registrations: Registration[]}>(`/api/v2/web/events/${eventId}/registrations`);
    return response.data.registrations || [];
  } catch (error) {
    console.error('Error getting event registrations:', error);
    return [];
  }
}

// Update an existing event
export async function updateEvent(eventId: string, eventData: any): Promise<Event> {
  try {
    const response = await apiClient.put<Event>(`/api/v2/web/events/${eventId}`, eventData);
    return response.data;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
}

// TimeTable functions
export async function getTimeTable(eventId: string): Promise<TimeTableResponse> {
  try {
    const response = await apiClient.get<TimeTableResponse>(`/api/v2/web/events/${eventId}/timetable`);
    return response.data;
  } catch (error) {
    console.error('Error getting timetable:', error);
    throw error;
  }
}

export async function updateTimeTable(eventId: string, timetable: TimeTableRequest): Promise<{ message: string }> {
  try {
    const response = await apiClient.put<{ message: string }>(`/api/v2/web/events/${eventId}/timetable`, timetable);
    return response.data;
  } catch (error) {
    console.error('Error updating timetable:', error);
    throw error;
  }
}

export async function addTimeTableEntry(eventId: string, entry: TimeEntry): Promise<{ message: string; entry_id: string }> {
  try {
    const response = await apiClient.post<{ message: string; entry_id: string }>(`/api/v2/web/events/${eventId}/timetable/entry`, entry);
    return response.data;
  } catch (error) {
    console.error('Error adding timetable entry:', error);
    throw error;
  }
}

export async function updateTimeTableEntry(eventId: string, entryId: string, entry: TimeEntry): Promise<{ message: string }> {
  try {
    const response = await apiClient.put<{ message: string }>(`/api/v2/web/events/${eventId}/timetable/entry/${entryId}`, entry);
    return response.data;
  } catch (error) {
    console.error('Error updating timetable entry:', error);
    throw error;
  }
}

export async function deleteTimeTableEntry(eventId: string, entryId: string): Promise<{ message: string }> {
  try {
    const response = await apiClient.delete<{ message: string }>(`/api/v2/web/events/${eventId}/timetable/entry/${entryId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting timetable entry:', error);
    throw error;
  }
}

// Export as eventApi for consistency with the React component
export const eventApi = {
  getEvents,
  createEvent,
  cancelEventRegistration,
  getUserEventRegistrations,
  getEventViewData,
  sendVotingEmail,
  generateMatches,
  generateGroupMatches,
  getGroupMatches,
  downloadEventExcel,
  downloadVotesExcel,
  deleteGroup,
  addUserToGroup,
  removeUserFromGroup,
  getUnmatchedUsers,
  getUnregisteredUsers,
  registerUserForEvent,
  unregisterUserFromEvent,
  getEventRegistrations,
  updateEvent,
  getTimeTable,
  updateTimeTable,
  addTimeTableEntry,
  updateTimeTableEntry,
  deleteTimeTableEntry
};
